import LogoDefensoria from '/img/LOGO_DPE_2024_ok.png';
import type { ReadCandidatoDto } from '@/api.types';
import { DataAmigavel, DataEHoraAmigavel } from '@/shared/utils/datas';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { styles } from './CandidatoDetalhesPDFstyle';
import {
  formatarContatoCandidato,
  formatarCpfExibicao,
  formatarEstadoCivilExibicao,
  formatarGeneroCandidato,
  formatarRacaCandidato,
  formatarSituacaoCandidato,
  getEnderecoExibicao,
} from '../../candidatoDetalhes.utils';

export interface CandidatoDetalhesPdfProps {
  candidato: ReadCandidatoDto;
  processoSeletivo: { nome: string; provaUrl?: string | null } | null;
  cursoPretendidoNomeExtra: string | null;
  instituicaoRazaoSocialExtra: string | null;
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
  return (
    <View style={styles.col}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={hasValue ? styles.fieldValue : styles.fieldValueMuted}>{hasValue ? String(value) : '-'}</Text>
    </View>
  );
}

export function CandidatoDetalhesPdf({
  candidato,
  processoSeletivo,
  cursoPretendidoNomeExtra,
  instituicaoRazaoSocialExtra,
}: CandidatoDetalhesPdfProps) {
  const endereco = getEnderecoExibicao(candidato);
  const cursoPretendido = candidato.cursoPretendidoNome || cursoPretendidoNomeExtra || '-';
  const instituicaoEnsino = candidato.instituicaoEnsinoNome || instituicaoRazaoSocialExtra || '-';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={LogoDefensoria} style={styles.logo} />
          <View style={styles.headerText}>
            <Text style={styles.title}>DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE</Text>
            <Text style={styles.subtitle}>DETALHES DO CANDIDATO</Text>
            {processoSeletivo?.nome ? (
              <Text style={styles.meta}>Processo Seletivo: {processoSeletivo.nome}</Text>
            ) : (
              <Text style={styles.meta}>Processo Seletivo: -</Text>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Identificação Pessoal</Text>
          <View style={styles.row}>
            <Field label="Número do candidato" value={candidato.id ?? '-'} />
            <Field label="CPF" value={formatarCpfExibicao(candidato.pessoa?.cpf)} />
            <Field label="Situação" value={formatarSituacaoCandidato(candidato.situacao)} />
          </View>
          <View style={styles.row}>
            <Field label="Nome completo" value={candidato.pessoa?.nome || '-'} />
            <Field label="Nome social" value={candidato.nomeSocial?.trim() ? candidato.nomeSocial : '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Gênero" value={formatarGeneroCandidato(candidato.genero)} />
            <Field label="Estado civil" value={formatarEstadoCivilExibicao(candidato.pessoa?.estadocivil)} />
            <Field label="Data de nascimento" value={DataAmigavel(candidato.pessoa?.dataNascimento) || '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Contato" value={formatarContatoCandidato(candidato)} />
            <Field label="E-mail" value={candidato.pessoa?.email || '-'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Dados Acadêmicos</Text>
          <View style={styles.row}>
            <Field label="Instituição de Ensino" value={instituicaoEnsino} />
            <Field label="Período atual do curso" value={candidato.periodoAtualCurso ?? '-'} />
            <Field label="Ano de início do curso" value={candidato.anoInicioCurso ?? '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Curso pretendido" value={cursoPretendido} />
            <Field label="Município" value={candidato.municipio || '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Declaração de matrícula (anexo)" value={candidato.documentoEstudoUrl ? 'Sim' : 'Não'} />
            <Field label="Documento de identificação (anexo)" value={candidato.documentoIdentificacaoUrl ? 'Sim' : 'Não'} />
            <Field label="Prova enviada (anexo)" value={candidato.provaUrl ? 'Sim' : 'Não'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Endereço</Text>
          <View style={styles.row}>
            <Field label="CEP" value={endereco?.cep?.trim() ? endereco.cep : '-'} />
            <Field label="Número" value={endereco?.numero ?? '-'} />
            <Field label="Bairro" value={endereco?.bairro?.trim() ? endereco.bairro : '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Logradouro" value={endereco?.logradouro?.trim() ? endereco.logradouro : '-'} />
            <Field label="Complemento" value={endereco?.complemento?.trim() ? endereco.complemento : '-'} />
          </View>
          <View style={styles.row}>
            <Field label="Cidade" value={endereco?.cidade?.trim() ? endereco.cidade : '-'} />
            <Field label="Estado" value={endereco?.estado?.trim() ? endereco.estado : '-'} />
            <Field label="Comprovante de residência (anexo)" value={candidato.comprovanteResidenciaUrl ? 'Sim' : 'Não'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ações Afirmativas / PcD</Text>
          <View style={styles.row}>
            <Field label="Identificação racial" value={formatarRacaCandidato(candidato.raca)} />
            <Field label="Vagas para pessoas negras" value={candidato.optaVagasNegros ? 'Sim' : 'Não'} />
            <Field label="Declara-se indígena" value={candidato.declaraIndigena ? 'Sim' : 'Não'} />
          </View>
          <View style={styles.row}>
            <Field label="Documentação indígena (anexo)" value={candidato.documentacaoIndigenaUrl ? 'Sim' : 'Não'} />
            <Field label="Vagas PcD" value={candidato.optaVagasPcd ? 'Sim' : 'Não'} />
            <Field label="Laudo médico (anexo)" value={candidato.laudoMedicoUrl ? 'Sim' : 'Não'} />
          </View>
          {candidato.optaVagasPcd && (
            <View style={styles.row}>
              <Field label="Descrição da deficiência" value={candidato.descricaoDeficiencia || '-'} />
              <Field label="Especificação" value={candidato.especificacaoDeficiencia || '-'} />
              <Field label="Cuidados especiais para prova" value={candidato.necessitaCuidadosEspeciais ? 'Sim' : 'Não'} />
            </View>
          )}
          <View style={styles.row}>
            <Field label="Documentos complementares (anexo)" value={candidato.documentosComplementaresUrl ? 'Sim' : 'Não'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Declarações</Text>
          <View style={styles.row}>
            <Field label="Veracidade das informações" value={candidato.declaracaoVeracidade ? 'Confirmada' : 'Não confirmada'} />
            <Field label="Ciência do edital" value={candidato.confirmacaoEdital ? 'Confirmada' : 'Não confirmada'} />
            <Field label="Consentimento LGPD" value={candidato.consentimentoLgpd ? 'Confirmado' : 'Não confirmado'} />
          </View>
        </View>

        <View style={styles.footer}>
          <Text>SGI - Sistema de Gestão Interna</Text>
          <Text>Gerado em: {new Date().toLocaleString()}</Text>
        </View>

        <View style={{ marginTop: 6 }}>
          <Text style={styles.meta}>Inscrição: {DataEHoraAmigavel(candidato.dataInscricao) || '-'}</Text>
          <Text style={styles.meta}>Última atualização: {DataEHoraAmigavel(candidato.editadoEm) || '-'}</Text>
        </View>
      </Page>
    </Document>
  );
}

